import { useEffect, useRef, useState } from 'react';
import styles from './styles.module.scss';
import { useTranslation } from 'react-i18next';

interface ChatMessage {
    id: number;
    question: string;
    answer: string;
    showAnswer: boolean;
}

interface ChatFieldProps {
    selectedQuestion?: string | null;
    selectedAnswer?: string | null;
}

const ChatField = ({ selectedQuestion, selectedAnswer }: ChatFieldProps) => {
    const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
    const messagesContainerRef = useRef<HTMLDivElement>(null);
    const { t } = useTranslation();

    // Function to scroll to bottom with smooth animation
    const scrollToBottom = () => {
        // Find the parent section with overflow-y-auto
        const parentSection = messagesContainerRef.current?.closest('section');
        if (parentSection) {
            // Calculate the distance to scroll
            const currentScrollTop = parentSection.scrollTop;
            const targetScrollTop = parentSection.scrollHeight - parentSection.clientHeight;
            const scrollDistance = targetScrollTop - currentScrollTop;

            // Only scroll if there's actually a distance to scroll
            if (scrollDistance > 0) {
                // Use smooth scrolling with scrollTo for better performance
                parentSection.scrollTo({
                    top: targetScrollTop,
                    behavior: 'smooth',
                });

                // Fallback: If scrollTo doesn't work smoothly, use scrollIntoView
                setTimeout(() => {
                    if (parentSection.scrollTop < targetScrollTop - 10) {
                        const lastMessage = messagesContainerRef.current?.lastElementChild;
                        if (lastMessage) {
                            lastMessage.scrollIntoView({
                                behavior: 'smooth',
                                block: 'end',
                                inline: 'nearest',
                            });
                        }
                    }
                }, 150);
            }
        }
    };

    // Scroll to bottom when new messages are added
    useEffect(() => {
        if (chatMessages.length > 0) {
            // Use a longer delay to ensure smooth animation
            setTimeout(() => {
                scrollToBottom();
            }, 100);
        }
    }, [chatMessages]);

    useEffect(() => {
        if (selectedQuestion && selectedAnswer) {
            const newMessage: ChatMessage = {
                id: Date.now(),
                question: selectedQuestion,
                answer: selectedAnswer,
                showAnswer: false,
            };

            setChatMessages((prev) => [...prev, newMessage]);

            // Show answer after a delay to create natural chat flow
            setTimeout(() => {
                setChatMessages((prev) => prev.map((msg) => (msg.id === newMessage.id ? { ...msg, showAnswer: true } : msg)));
            }, 800); // 800ms delay before showing answer
        }
    }, [selectedQuestion, selectedAnswer]);

    return (
        <div className={styles['chat-field-container']}>
            <div ref={messagesContainerRef} className={styles['messages-container']}>
                {/* Welcome message - always displayed */}
                <div className={`${styles['welcome-container']} focus-border`} aria-label={t('faq.chat-field.welcome')}>
                    <div className={styles['welcome-tail']}></div>
                    <div className={styles['welcome-bubble']}>
                        <p className={styles['welcome-text']}>{t('faq.chat-field.welcome')}</p>
                    </div>
                </div>

                {/* Chat messages from selected questions */}
                {chatMessages.map((message) => (
                    <div key={message.id} className={styles['message-group']}>
                        {/* Question - Dark bubble on right */}
                        <div className={styles['question-container']}>
                            <div className="focus-border flex" aria-label={message.question}>
                                <div className={styles['question-bubble']}>
                                    <p className={styles['question-text']}>{message.question}</p>
                                </div>
                                <div className={styles['question-tail']}></div>
                            </div>
                        </div>

                        {/* Answer - White bubble on left - only show when showAnswer is true */}
                        {message.showAnswer && (
                            <div className={styles['answer-container']}>
                                <div className="focus-border flex" aria-label={message.answer}>
                                    <div className={styles['answer-tail']}></div>
                                    <div className={styles['answer-bubble']}>
                                        <div className={styles['answer-text']} dangerouslySetInnerHTML={{ __html: message.answer }} />
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default ChatField;
